import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SideSheet from "@/components/SideSheet";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { isoDate, localToday, parseIso } from "@/lib/dates";
import { useAuth, canWrite } from "@/context/AuthContext";
import { joinMeta, plural } from "@/lib/utils";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const DAY_NAMES = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

// A month cell fits three entries. Everything past that used to collapse into a
// "+N more" label with nothing behind it, so on a busy day most of the schedule
// could not be reached from the calendar at all.
const PER_DAY = 3;

// distinct unit colors (WCAG-friendly, work in both themes)
const UNIT_COLORS = [
  { bg: "bg-blue-500/15 hover:bg-blue-500/25", dot: "bg-blue-500", text: "text-blue-700 dark:text-blue-300" },
  { bg: "bg-emerald-500/15 hover:bg-emerald-500/25", dot: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-300" },
  { bg: "bg-violet-500/15 hover:bg-violet-500/25", dot: "bg-violet-500", text: "text-violet-700 dark:text-violet-300" },
  { bg: "bg-amber-500/15 hover:bg-amber-500/25", dot: "bg-amber-500", text: "text-amber-700 dark:text-amber-300" },
  { bg: "bg-rose-500/15 hover:bg-rose-500/25", dot: "bg-rose-500", text: "text-rose-700 dark:text-rose-300" },
];

export default function CalendarPage() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const [sp, setSp] = useSearchParams();
  // The month and the open day live in the URL: a refresh lands on the same month and
  // a pasted link opens the same day, the way the register's filters already travel.
  const cursor = useMemo(() => {
    const m = /^(\d{4})-(\d{2})$/.exec(sp.get("month") || "");
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, 1) : new Date();
    d.setDate(1);
    return d;
  }, [sp]);
  const dayOpen = sp.get("day") || null;
  const unitFilter = sp.get("unit_id") || "all";
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [colorMode, setColorMode] = useState("unit");
  const nav = useNavigate();

  const write = (patch) => setSp((prev) => {
    const next = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(patch)) {
      if (v && v !== "all") next.set(k, v); else next.delete(k);
    }
    return next;
  }, { replace: true });

  const load = async () => {
    const year = cursor.getFullYear(), month = cursor.getMonth();
    // The month's own last day. Asking up to the 1st of the next month fetched one extra
    // day's trainings — the API's date_to is inclusive — that the grid never drew.
    const params = { date_from: isoDate(new Date(year, month, 1)), date_to: isoDate(new Date(year, month + 1, 0)) };
    const [t, u] = await Promise.all([api.get("/trainings", { params }), api.get("/units")]);
    setItems(t.data);
    setUnits(u.data);
  };
  useEffect(() => { load(); }, [cursor]); // eslint-disable-line react-hooks/exhaustive-deps

  const unitColor = useMemo(() => {
    const m = {};
    units.forEach((u, i) => { m[u.id] = UNIT_COLORS[i % UNIT_COLORS.length]; });
    return m;
  }, [units]);
  const typeColor = { INTERNAL: UNIT_COLORS[0], EXTERNAL: UNIT_COLORS[2] };

  // Every unit the training is held for, as the register's unit filter reads it. On
  // the primary alone, a session held for two plants vanished under the second one.
  const filtered = items.filter((t) => unitFilter === "all"
    || (t.unit_ids || []).includes(unitFilter) || t.unit_id === unitFilter);
  const grid = useMemo(() => {
    const year = cursor.getFullYear(), month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const startWeekday = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    filtered.forEach((t) => {
      const key = t.training_date;
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [filtered]);

  // Local, not UTC: toISOString() ringed tomorrow as today from 18:30 IST onwards.
  const today = localToday();
  const shift = (n) => {
    const c = new Date(cursor); c.setMonth(c.getMonth() + n);
    write({ month: `${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, "0")}`, day: "" });
  };

  const prettyDay = (key) => {
    const dt = parseIso(key);
    if (!dt) return "";
    return `${DAY_NAMES[dt.getDay()]}, ${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
  };

  const newOn = (day) => nav(`/trainings/new?date=${day}${unitFilter !== "all" ? `&unit_id=${unitFilter}` : ""}`);

  // One renderer for both the month cell and the day list, so an entry cannot come
  // to mean two different things depending on where it is drawn.
  const entry = (t, { full = false } = {}) => {
    const col = colorMode === "unit" ? (unitColor[t.unit_id] || UNIT_COLORS[0]) : (typeColor[t.training_type] || UNIT_COLORS[0]);
    // A training that was called off or refused still occupies its date, and looked
    // exactly like one that is going ahead. Struck through and muted rather than
    // hidden: the slot being taken is worth knowing.
    const off = t.workflow_status === "Cancelled";
    return (
      <button key={t.id} onClick={(e) => { e.stopPropagation(); nav(`/trainings/${t.id}`); }}
        title={off ? `${t.title} — ${t.workflow_status}` : t.title}
        className={`w-full text-left px-2 py-1 rounded text-xs ${full ? "" : "truncate"} ${off ? "bg-muted text-muted-foreground line-through" : `${col.bg} ${col.text}`}`}
        data-testid={`cal-event-${t.id}`}>
        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${off ? "bg-muted-foreground" : col.dot}`} />{t.title}
        {full && (
          <span className="block pl-3 pt-0.5 text-[11px] text-muted-foreground">
            {joinMeta((t.unit_names || []).join(", ") || t.unit_name, t.category, t.trainer_name, t.workflow_status)}
          </span>
        )}
      </button>
    );
  };

  const openEvents = dayOpen ? (eventsByDay[dayOpen] || []) : [];
  const openIsPast = dayOpen && dayOpen < today;

  return (
    <div className="space-y-5" data-testid="calendar-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-primary">Schedule</div>
          <h2 className="font-heading font-bold text-3xl mt-1">Training Calendar</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Month view of all trainings, colour-coded. Click any date to see what is on it
            {writable ? " or to put a new training there." : "."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="cal-prev" aria-label="Previous month"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="font-heading font-semibold text-lg w-40 text-center" data-testid="cal-title">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</div>
          <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="cal-next" aria-label="Next month"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => write({ month: "", day: "" })} data-testid="cal-today">Today</Button>
          {writable && (
            <Button size="sm" onClick={() => newOn(today)} data-testid="cal-new-training">
              <Plus className="w-4 h-4 mr-1.5" /> New training
            </Button>
          )}
        </div>
      </div>

      <Card className="p-4 border border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select value={unitFilter} onValueChange={(v) => write({ unit_id: v })}>
            <SelectTrigger data-testid="cal-filter-unit" aria-label="Filter calendar by unit"><SelectValue placeholder="Unit" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All units</SelectItem>
              {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={colorMode} onValueChange={setColorMode}>
            <SelectTrigger data-testid="cal-color-mode" aria-label="Colour the calendar by"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unit">Colour by unit</SelectItem>
              <SelectItem value="type">Colour by type</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-3 text-xs flex-wrap">
            {colorMode === "unit" ? units.slice(0, 5).map((u, i) => (
              <div key={u.id} className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${UNIT_COLORS[i % 5].dot}`} />{u.name.split(" - ")[0]}</div>
            )) : (
              <>
                <div className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${typeColor.INTERNAL.dot}`} />Internal</div>
                <div className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${typeColor.EXTERNAL.dot}`} />External</div>
              </>
            )}
          </div>
        </div>
      </Card>

      <Card className="border border-border overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border bg-muted/60">
          {WEEKDAYS.map((w) => <div key={w} className="px-3 py-2 text-xs font-medium overline text-muted-foreground">{w}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((d, idx) => {
            const key = d ? isoDate(d) : `pad-${idx}`;
            const events = d ? (eventsByDay[isoDate(d)] || []) : [];
            const isToday = d && isoDate(d) === today;
            const hidden = events.length - PER_DAY;
            const number = isToday
              ? <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground">{d?.getDate()}</span>
              : d?.getDate();
            return (
              // The whole cell opens the day, empty or not — an empty day used to be inert
              // text, so there was nowhere on the calendar to start a training from.
              <div key={key}
                   className={`min-h-[110px] p-2 border-b border-r border-border ${d ? "cursor-pointer hover:bg-muted/40" : "bg-muted/20"}`}
                   onClick={d ? () => write({ day: isoDate(d) }) : undefined}
                   data-testid={d ? `cal-day-${isoDate(d)}` : undefined}>
                {d && (
                  <div className={`text-xs font-medium mb-1 ${isToday ? "text-primary" : "text-muted-foreground"}`}>
                    <button onClick={(e) => { e.stopPropagation(); write({ day: isoDate(d) }); }}
                            data-testid={`cal-open-day-${isoDate(d)}`}
                            aria-label={events.length
                              ? `Show all ${events.length} trainings on ${prettyDay(isoDate(d))}`
                              : `Open ${prettyDay(isoDate(d))}`}
                            className="rounded hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                      {number}
                    </button>
                  </div>
                )}
                <div className="space-y-1">
                  {events.slice(0, PER_DAY).map((t) => entry(t))}
                  {hidden > 0 && (
                    <button onClick={(e) => { e.stopPropagation(); write({ day: isoDate(d) }); }} data-testid={`cal-more-${isoDate(d)}`}
                      className="text-xs text-muted-foreground hover:text-primary pl-2 underline underline-offset-2">
                      +{hidden} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <SideSheet open={!!dayOpen} onOpenChange={(v) => !v && write({ day: "" })} testid="cal-day-sheet"
                 title={prettyDay(dayOpen)}
                 description={openEvents.length === 0 ? "Nothing scheduled on this day"
                   : `${plural(openEvents.length, "training")} on this day${unitFilter !== "all" ? " — the unit filter still applies" : ""}`}
                 footer={writable && (
                   <Button onClick={() => newOn(dayOpen)} data-testid="cal-day-new">
                     <Plus className="w-4 h-4 mr-1.5" /> {openIsPast ? "Record a training on this day" : "New training on this day"}
                   </Button>
                 )}>
        <div className="space-y-1.5">
          {openEvents.map((t) => entry(t, { full: true }))}
          {openEvents.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">
              {writable ? "Free. Put a training here from the button below." : "Nothing here."}
            </p>
          )}
        </div>
      </SideSheet>
    </div>
  );
}
